/**
 * Der Check-in-Reiter — die Mappe für die Tauchbasis.
 *
 * **Kein Passwort, keine Sperre, kein Zwischenschritt.** Wer den Reiter
 * aufschlägt, sieht seine Unterlagen. Die Begründung steht in `checkin.js`;
 * für die Oberfläche zählt die Folge: Es gibt nur noch einen Zustand, und der
 * Foto-Upload funktioniert wieder auf dem Telefon.
 *
 * **Kacheln, und in jeder Kachel das Bild.** Eine Zeile mit Text beantwortet
 * die Frage am Tresen nicht — dort wird die Karte gezeigt, nicht ihre Nummer
 * vorgelesen. Die Beilage ist deshalb das Erste in der Kachel, gross genug zum
 * Erkennen und mit einem Tipp bildschirmfüllend.
 *
 * **Die Sicherungszeile steht ganz oben, nicht im Sync-Reiter.** Ein Bereich,
 * der stillschweigend nur im Browserspeicher liegt, ist eine Falle.
 */

import { ARTEN, art as artVon, ausText, entschluesselAlles, groesse, gruppiere,
  holeTresor, kennzahlen, laeuftAb, liste, loesche, sicherungslage, speichere,
  vergissTresor } from '../checkin.js';
import { entsperre } from '../krypto.js';
import { alsAnhang } from '../bilder.js';
import { gleicheCheckinAb } from '../checkinsync.js';
import { SCHLUESSEL_ZULETZT, checkinAdapter, gewaehlterAnbieter, istVerbunden, vergissRecht }
  from './sync.js';
import { ANBIETER, stelleBinaeresHer } from '../speicher.js';
import { kennzahl } from './kennzahl.js';
import { alsBlatt } from './blatt.js';
import { symbolHtml } from '../symbole.js';

function datum(text) {
  if (!text) return '';
  const zeit = new Date(text);
  return Number.isNaN(zeit.getTime()) ? text : zeit.toLocaleDateString('de-CH');
}

/** Die Zeile unter dem Titel: Aussteller und Gültigkeit, so benannt wie die Art. */
export function metazeile(unterlage) {
  const eineArt = artVon(unterlage.art);
  const teile = [];
  if (unterlage.aussteller) teile.push(unterlage.aussteller);
  if (unterlage.gueltig_von && eineArt?.von) {
    teile.push(`${eineArt.von} ${datum(unterlage.gueltig_von)}`);
  }
  if (unterlage.gueltig_bis && eineArt?.bis) {
    teile.push(`${eineArt.bis} ${datum(unterlage.gueltig_bis)}`);
  }
  return teile.join(' · ');
}

/** Ein Bild aus einer Beilage — als Adresse, die der Aufrufer wieder freigibt. */
function bildAdresse(anhang) {
  return URL.createObjectURL(new Blob([ausText(anhang.daten)], { type: anhang.typ }));
}

export function ansichtCheckin(db, tauchgaenge) {
  const wurzel = document.createElement('section');
  wurzel.className = 'checkin';
  const einstellungen = globalThis.localStorage;
  const zahlen = kennzahlen(tauchgaenge);
  // Was der Browser fuer uns behaelt, bis die Ansicht verschwindet.
  let adressen = [];

  function gibAdressenFrei() {
    for (const adresse of adressen) URL.revokeObjectURL(adresse);
    adressen = [];
  }

  function merkeAdresse(adresse) {
    adressen.push(adresse);
    return adresse;
  }

  function kopf() {
    return `
      <header class="checkin-kopf">
        <h1>Check-in</h1>
        <button type="button" class="sicherungszeile"></button>
      </header>
      <div class="kennzahlen">
        ${kennzahl(zahlen.anzahl, 'Tauchgänge', { zeichen: 'liste' })}
        ${kennzahl(zahlen.letzte12Monate, 'letzte 12 Monate', { zeichen: 'zeit' })}
        ${kennzahl(datum(zahlen.letzter) || '—', 'letzter Tauchgang', { zeichen: 'kalender' })}
        ${kennzahl((zahlen.erster ?? '').slice(0, 4) || '—', 'tauche seit',
    { zeichen: 'rekord' })}
      </div>`;
  }

  // --- Sicherung ----------------------------------------------------------

  function lage() {
    const wahl = gewaehlterAnbieter(einstellungen);
    return sicherungslage({
      anbieter: ANBIETER[wahl]?.name ?? null,
      verbunden: istVerbunden(),
      zuletzt: einstellungen.getItem(SCHLUESSEL_ZULETZT),
    });
  }

  /**
   * Die Zeile im Kopf ist selbst der Knopf.
   *
   * Als eigener Kasten unter den Kennzahlen nahm sie 147 Pixel — mit Rahmen,
   * dreizeiligem Satz und einem Knopf daneben —, und die erste Unterlage
   * begann erst bei y=542 auf einem 915 Pixel hohen Schirm. Sie steht jetzt
   * dort, wo vorher „Die Angaben, nach denen an der Tauchbasis gefragt wird"
   * stand: eine Zeile, die niemand zweimal liest, gegen eine, die etwas sagt.
   *
   * Der ganze Satz bleibt als `title` daran hängen — im Kopf steht die
   * Kurzform, und die Warnung darf nicht zur Bedienungsanleitung werden.
   */
  function zeichneSicherung(meldung = '') {
    const zeile = wurzel.querySelector('.sicherungszeile');
    if (!zeile) return;
    const zustand = lage();
    zeile.className = `sicherungszeile ${zustand.stufe}`;
    zeile.title = `${zustand.text} — ${zustand.knopf}`;
    zeile.innerHTML = `
      <span class="sicherung-zeichen">${symbolHtml(
    zustand.stufe === 'gesichert' ? 'haken' : 'sync', 16)}</span>
      <span class="sicherung-text">${meldung || zustand.kurz}</span>`;
    zeile.onclick = () => {
      if (zustand.stufe !== 'gesichert') {
        location.hash = '#/sync';
        return;
      }
      gleicheAb('Wird gesichert …');
    };
  }

  /**
   * Lesen und schreiben in einem — und deshalb auch beim Aufschlagen.
   *
   * Ausgelöst wurde der Abgleich bisher nur auf eine **eigene** Änderung oder
   * auf einen Tipp. Auf dem zweiten Gerät ändert sich aber nichts: Dort will
   * man sehen, was am ersten entstanden ist. Wer den Reiter öffnete und
   * angemeldet war, sah trotzdem nur den lokalen Stand — genau der gemeldete
   * Fehler. Ein Abgleich beim Aufschlagen kostet eine Datei-Lesung und
   * erspart die Frage, warum nichts ankommt.
   */
  async function gleicheAb(meldung) {
    if (!istVerbunden()) return;
    zeichneSicherung(meldung);
    try {
      const ergebnis = await gleicheCheckinAb(db, checkinAdapter(),
        { stelleHer: stelleBinaeresHer });
      einstellungen.setItem(SCHLUESSEL_ZULETZT, new Date().toISOString());
      if (ergebnis.erstmalig) {
        zeichneSicherung(`Abgelegt: ${ergebnis.anzahl} Unterlagen`);
      } else {
        await zeichne();
      }
    } catch (fehler) {
      // Ein abgelaufenes Recht muss weg, sonst sagt die Zeile weiter
      // „gesichert" und der nächste Griff scheitert genauso.
      if (fehler?.rechtWeg) vergissRecht();
      zeichneSicherung(`Nicht abgeglichen: ${fehler.message}`);
    }
  }

  // --- Zeichnen -----------------------------------------------------------

  async function zeichne() {
    gibAdressenFrei();
    const tresor = await holeTresor(db);
    if (tresor) {
      zeichneUebernahme(tresor);
      return;
    }
    zeichneMappe(await liste(db));
  }

  /**
   * Der einmalige Übergang aus der Zeit des Passworts.
   *
   * Wer den Bereich damals eingerichtet hat, gibt es noch ein letztes Mal ein.
   * Der Ausweg daneben ist bewusst hart formuliert: Ohne Passwort ist der
   * Geheimtext nicht zu retten, und ein „vielleicht später" hielte den Reiter
   * für immer in diesem Zustand fest.
   */
  function zeichneUebernahme(tresor) {
    wurzel.innerHTML = `${kopf()}
      <section class="block-weit einrichten">
        <h2>Passwort wird nicht mehr gebraucht</h2>
        <p class="fliess">Dieser Bereich lag bisher verschlüsselt hinter einem
          Passwort und sperrte sich von selbst. Beides ist weg: Das Gerät ist
          schon geschützt, und die Sperre verhinderte auf dem Telefon den
          Foto-Upload.</p>
        <p class="fliess">Zum Übernehmen des Bestands braucht es das Passwort
          ein <strong>letztes Mal</strong>. Danach liegen die Unterlagen im
          Klartext auf diesem Gerät und im Cloud-Ordner.</p>
        <form class="checkin-form uebernehmen">
          <label>Passwort <input type="password" name="pw" autocomplete="current-password"
            required></label>
          <button type="submit" class="knopf">Übernehmen</button>
          <button type="button" class="loeschen verwerfen">alles verwerfen</button>
          <p class="checkin-fehler" role="alert"></p>
        </form>
      </section>`;
    zeichneSicherung();
    const fehler = wurzel.querySelector('.checkin-fehler');
    wurzel.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();
      fehler.textContent = 'Schlüssel wird abgeleitet …';
      try {
        const schluessel = await entsperre(e.target.elements.pw.value, tresor);
        const anzahl = await entschluesselAlles(db, schluessel);
        fehler.textContent = `${anzahl} Unterlagen übernommen.`;
        zeichne();
      } catch (ausnahme) {
        fehler.textContent = ausnahme.message;
      }
    });
    wurzel.querySelector('.verwerfen').addEventListener('click', async () => {
      if (!globalThis.confirm('Alle bisherigen Unterlagen endgültig verwerfen? '
        + 'Ohne das Passwort sind sie nicht zu retten.')) return;
      for (const satz of await liste(db)) await loesche(db, satz.id);
      await vergissTresor(db);
      zeichne();
    });
  }

  function zeichneMappe(unterlagen) {
    wurzel.innerHTML = `${kopf()}<div class="checkin-gruppen"></div>`;
    zeichneSicherung();
    const bereich = wurzel.querySelector('.checkin-gruppen');
    for (const { art: eineArt, eintraege } of gruppiere(unterlagen)) {
      const gruppe = document.createElement('section');
      gruppe.className = 'checkin-gruppe';
      gruppe.innerHTML = `<h2>${symbolHtml(eineArt.zeichen, 20)} ${eineArt.name}
        <button type="button" class="knopf klein neu" data-art="${eineArt.schluessel}"
          aria-label="${eineArt.name} hinzufügen">+</button></h2>
        <div class="checkin-kacheln"></div>`;
      const kacheln = gruppe.querySelector('.checkin-kacheln');
      for (const u of eintraege) kacheln.append(kachel(u, eineArt));
      if (eintraege.length === 0) {
        kacheln.innerHTML = '<p class="leer-leise">nichts hinterlegt</p>';
      }
      bereich.append(gruppe);
    }
    for (const knopf of wurzel.querySelectorAll('.neu')) {
      knopf.addEventListener('click', () => formular(knopf.dataset.art, null));
    }
  }

  /**
   * Eine Kachel: oben das Bild, darunter die Angaben.
   *
   * Das Bild steht vorn, weil es an der Basis das Gefragte ist. Fehlt es,
   * nimmt das Zeichen der Art seinen Platz ein — eine Kachel ohne Kopf sähe
   * aus wie ein Ladefehler.
   */
  function kachel(u, eineArt) {
    const el = document.createElement('article');
    el.className = 'checkin-kachel';
    const bilder = u.anhaenge.filter((a) => String(a.typ ?? '').startsWith('image/'));
    const dateien = u.anhaenge.filter((a) => !String(a.typ ?? '').startsWith('image/'));

    const kopfteil = document.createElement('div');
    kopfteil.className = bilder.length ? 'kachel-bild' : 'kachel-bild leer';
    if (bilder.length) {
      for (const bild of bilder) {
        const img = document.createElement('img');
        img.src = merkeAdresse(bildAdresse(bild));
        img.alt = `${bild.name} zu ${u.titel}`;
        img.loading = 'lazy';
        img.addEventListener('click', () => zeigeGross(u, bilder, bilder.indexOf(bild)));
        kopfteil.append(img);
      }
    } else {
      kopfteil.innerHTML = symbolHtml(eineArt.zeichen, 40);
    }

    const felder = eineArt.geheim
      .filter(([name]) => u.felder?.[name])
      .map(([name, beschriftung]) => `<div class="feld"><dt>${beschriftung}</dt>`
        + `<dd>${u.felder[name]}</dd></div>`).join('');

    const rumpf = document.createElement('div');
    rumpf.className = 'kachel-rumpf';
    rumpf.innerHTML = `
      <h3>${u.titel || '(ohne Titel)'}</h3>
      <p class="checkin-meta">${metazeile(u)}${laeuftAb(u)
  ? ' <span class="marke bald">läuft ab</span>' : ''}</p>
      <dl>${felder}</dl>
      <div class="kachel-dateien">${dateien.map((d) => `<a class="beilage-holen"
        data-id="${d.id}" download="${d.name}">${symbolHtml('laden', 16)} ${d.name}
        <span class="beilage-groesse">${groesse(d.groesse)}</span></a>`).join('')}</div>
      <div class="checkin-knoepfe">
        <button type="button" class="knopf klein bearbeiten">Bearbeiten</button>
        <button type="button" class="loeschen">löschen</button>
      </div>`;
    for (const verweis of rumpf.querySelectorAll('.beilage-holen')) {
      const datei = dateien.find((d) => d.id === verweis.dataset.id);
      verweis.href = merkeAdresse(bildAdresse(datei));
    }

    el.append(kopfteil, rumpf);
    rumpf.querySelector('.bearbeiten').addEventListener('click', () => formular(u.art, u));
    rumpf.querySelector('.loeschen').addEventListener('click', async () => {
      if (!globalThis.confirm(`„${u.titel || 'Diese Unterlage'}" endgültig löschen?`)) return;
      await loesche(db, u.id);
      zeichne();
    });
    return el;
  }

  /**
   * Ein Bild bildschirmfüllend — das ist der Moment am Tresen.
   *
   * Mit Vor und Zurück, weil eine Karte zwei Seiten hat und niemand zweimal
   * schliessen und wieder öffnen will.
   */
  function zeigeGross(u, bilder, start) {
    let stelle = start;
    const dialog = document.createElement('dialog');
    dialog.className = 'blattdialog beilagedialog';
    dialog.innerHTML = `<header class="kachelblatt-kopf">
        <h2>${u.titel || 'Beilage'}</h2>
        <button type="button" class="kachelblatt-zu" aria-label="Schliessen">✕</button>
      </header>
      <img alt="">
      <div class="beilage-blaettern">
        <button type="button" class="knopf klein zurueck">‹</button>
        <span class="beilage-stelle"></span>
        <button type="button" class="knopf klein weiter">›</button>
        <a class="knopf klein holen" download>herunterladen</a>
      </div>`;
    const bild = dialog.querySelector('img');
    const stellenzeile = dialog.querySelector('.beilage-stelle');
    const holen = dialog.querySelector('.holen');
    function zeigen() {
      const anhang = bilder[stelle];
      bild.src = merkeAdresse(bildAdresse(anhang));
      bild.alt = anhang.name;
      stellenzeile.textContent = `${stelle + 1} / ${bilder.length}`;
      holen.href = bild.src;
      holen.download = anhang.name;
    }
    dialog.querySelector('.zurueck').addEventListener('click', () => {
      stelle = (stelle - 1 + bilder.length) % bilder.length;
      zeigen();
    });
    dialog.querySelector('.weiter').addEventListener('click', () => {
      stelle = (stelle + 1) % bilder.length;
      zeigen();
    });
    if (bilder.length < 2) {
      dialog.querySelector('.zurueck').hidden = true;
      dialog.querySelector('.weiter').hidden = true;
      stellenzeile.hidden = true;
    }
    zeigen();
    dialog.querySelector('.kachelblatt-zu').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => dialog.remove());
    document.body.append(dialog);
    dialog.showModal();
  }

  // --- Formular -----------------------------------------------------------

  /**
   * Anlegen und Bearbeiten sind dasselbe Formular.
   *
   * Beilagen wandern beim Bearbeiten unverändert mit: Wer den Titel
   * korrigiert, soll nicht ein Megabyte neu durch die Kodierung schieben.
   */
  function formular(artSchluessel, bisher) {
    const eineArt = artVon(artSchluessel) ?? ARTEN[0];
    const anhaenge = bisher ? [...bisher.anhaenge] : [];
    const dialog = document.createElement('dialog');
    dialog.className = 'blattdialog checkin-dialog';
    dialog.innerHTML = `
      <header class="kachelblatt-kopf">
        <h2>${bisher ? 'Bearbeiten' : eineArt.name} <span class="leise">${
  bisher ? eineArt.name : 'hinzufügen'}</span></h2>
        <button type="button" class="kachelblatt-zu" aria-label="Schliessen">✕</button>
      </header>
      <form class="checkin-form gross">
        <div class="dialog-rumpf">
        <label>${eineArt.titel} <input name="titel" autocomplete="off"
          placeholder="${eineArt.titelBeispiel ?? ''}" value="${bisher?.titel ?? ''}"></label>
        ${eineArt.aussteller ? `<label>${eineArt.aussteller} <input name="aussteller"
          autocomplete="off" placeholder="${eineArt.ausstellerBeispiel ?? ''}"
          value="${bisher?.aussteller ?? ''}"></label>` : ''}
        ${eineArt.von ? `<label>${eineArt.von} <input name="gueltig_von" type="date"
          value="${bisher?.gueltig_von ?? ''}"></label>` : ''}
        ${eineArt.bis ? `<label>${eineArt.bis} <input name="gueltig_bis" type="date"
          value="${bisher?.gueltig_bis ?? ''}"></label>` : ''}
        ${eineArt.geheim.map(([name, beschriftung]) => `<label>${beschriftung}
          <input name="feld_${name}" autocomplete="off"
            value="${String(bisher?.felder?.[name] ?? '').replace(/"/g, '&quot;')}"></label>`)
    .join('')}
        <div class="anhang-bereich">
          <p class="anhang-titel">Beilagen <span class="leise">Foto oder PDF</span></p>
          <ul class="anhang-liste"></ul>
          <label class="anhang-waehlen">${symbolHtml('plus', 18)} Foto oder PDF wählen
            <input name="dateien" type="file" accept="image/*,application/pdf" multiple></label>
        </div>
        </div>
        <div class="dialog-fuss">
          <button type="submit" class="knopf">Speichern</button>
          <p class="checkin-fehler" role="alert"></p>
        </div>
      </form>`;
    alsBlatt(dialog);
    document.body.append(dialog);

    const listenfeld = dialog.querySelector('.anhang-liste');
    function zeichneAnhaenge() {
      listenfeld.innerHTML = anhaenge.map((a, i) => `<li>
        <span class="anhang-name">${a.name}</span>
        <span class="anhang-groesse">${groesse(a.groesse ?? a.roh?.byteLength)}</span>
        <button type="button" class="anhang-weg" data-i="${i}">entfernen</button>
      </li>`).join('') || '<li class="leer-leise">noch keine</li>';
      for (const knopf of listenfeld.querySelectorAll('.anhang-weg')) {
        knopf.addEventListener('click', () => {
          anhaenge.splice(Number(knopf.dataset.i), 1);
          zeichneAnhaenge();
        });
      }
    }
    zeichneAnhaenge();

    dialog.querySelector('input[name=dateien]').addEventListener('change', async (e) => {
      const fehler = dialog.querySelector('.checkin-fehler');
      fehler.textContent = 'Bilder werden verkleinert …';
      for (const datei of [...e.target.files]) anhaenge.push(await alsAnhang(datei));
      e.target.value = '';
      fehler.textContent = '';
      zeichneAnhaenge();
    });

    dialog.querySelector('.kachelblatt-zu').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => dialog.remove());

    dialog.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const fehler = dialog.querySelector('.checkin-fehler');
      const eingaben = e.target.elements;
      const felder = {};
      for (const feld of e.target.querySelectorAll('[name^=feld_]')) {
        if (feld.value.trim()) felder[feld.name.replace('feld_', '')] = feld.value.trim();
      }
      try {
        fehler.textContent = 'Wird gespeichert …';
        await speichere(db, {
          id: bisher?.id,
          art: artSchluessel,
          titel: eingaben.titel.value.trim(),
          aussteller: eingaben.aussteller?.value.trim() ?? '',
          gueltig_von: eingaben.gueltig_von?.value ?? '',
          gueltig_bis: eingaben.gueltig_bis?.value ?? '',
        }, felder, anhaenge);
        dialog.close();
        zeichne();
      } catch (ausnahme) {
        fehler.textContent = `Nicht gespeichert: ${ausnahme.message}`;
      }
    });

    dialog.showModal();
  }

  // Erst der lokale Stand, damit sofort etwas dasteht — dann der vom Anbieter.
  zeichne().then(() => gleicheAb('Wird abgeglichen …'));
  wurzel.aufraeumen = gibAdressenFrei;
  return wurzel;
}
